/**
 * <media-player> — a video laid over a still, with one button for all of it.
 *
 * The still is the cover and never leaves: the video fades in over it and fades
 * back out when it ends, so "return to the image" costs nothing and can't get out
 * of step with what is on screen.
 *
 * Autoplay waits for the panel to actually be on screen. Firing it at page load
 * means the one thing it was meant to show has already happened by the time the
 * reader scrolls down — and it burns a video's worth of a phone's data for
 * someone who may never reach it.
 *
 * It plays once. A loop is a decoration; this is a demonstration, and the button
 * is there for anyone who wants it again.
 *
 * The scrubber and the fullscreen button are optional extras for tutorial footage,
 * where a viewer needs to go back over a step. Fullscreen hands over to the
 * browser's own controls, which already do volume, speed and captions better than
 * a theme can.
 */
(() => {
  class MediaPlayer extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('[data-media-play]');
      if (!this.video) return;

      // Set here rather than in the markup: muted is what makes autoplay legal,
      // and inline is what stops iOS opening its own fullscreen player.
      this.video.muted = true;
      this.video.playsInline = true;
      this.video.disablePictureInPicture = true;

      this.progress = this.querySelector('[data-media-progress]');
      this.fullscreenButton = this.querySelector('[data-media-fullscreen]');
      this.soundButton = this.querySelector('[data-media-sound]');

      this.button?.addEventListener('click', () => this.#toggle());
      if (this.progress) this.#wireProgress();
      if (this.fullscreenButton) this.#wireFullscreen();
      if (this.soundButton) this.#wireSound();
      this.video.addEventListener('play', () => this.#state('playing'));
      this.video.addEventListener('pause', () => {
        // `pause` also fires as part of ending; the ended handler owns that case.
        if (!this.video.ended) this.#state('paused');
      });
      this.video.addEventListener('ended', () => this.#state('ended'));

      if (this.hasAttribute('data-autoplay')) this.#watchForAutoplay();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.onFullscreenChange) {
        document.removeEventListener('fullscreenchange', this.onFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange);
      }
    }

    #wireProgress() {
      const progress = this.progress;

      const paint = () => {
        const { duration, currentTime } = this.video;
        // Live streams and not-yet-loaded metadata have no duration to divide by.
        if (!duration || !Number.isFinite(duration)) return;
        // Not while the viewer is holding it: playback would drag the handle out
        // from under their finger.
        if (!this.scrubbing && document.activeElement !== progress) {
          progress.value = String(Math.round((currentTime / duration) * 1000));
        }
        progress.style.setProperty('--progress', `${(progress.value / 1000) * 100}%`);
      };

      this.video.addEventListener('timeupdate', paint);
      this.video.addEventListener('loadedmetadata', paint);
      this.video.addEventListener('seeked', paint);

      progress.addEventListener('input', () => {
        const { duration } = this.video;
        if (duration && Number.isFinite(duration)) {
          this.video.currentTime = (progress.value / 1000) * duration;
        }
        progress.style.setProperty('--progress', `${(progress.value / 1000) * 100}%`);
        // Scrubbing a video still hidden behind its cover would move a frame
        // nobody can see.
        this.#showVideo();
      });

      progress.addEventListener('pointerdown', () => {
        this.scrubbing = true;
      });
      ['pointerup', 'pointercancel'].forEach((type) => {
        progress.addEventListener(type, () => {
          this.scrubbing = false;
        });
      });
    }

    #wireFullscreen() {
      this.fullscreenButton.addEventListener('click', () => {
        const video = this.video;
        // The browser's own controls, but only in fullscreen: in place, the
        // theme's buttons are the controls and two sets would overlap.
        video.controls = true;

        const request =
          video.requestFullscreen || video.webkitRequestFullscreen || video.webkitEnterFullscreen;
        if (!request) {
          video.controls = false;
          return;
        }

        Promise.resolve(request.call(video)).catch(() => {
          // Refused (no gesture, or blocked by permissions policy): put the
          // controls back rather than leaving a second set on the page.
          video.controls = false;
        });

        if (video.paused) this.#play();
      });

      this.onFullscreenChange = () => {
        if (!document.fullscreenElement && !document.webkitFullscreenElement) {
          this.video.controls = false;
        }
      };
      document.addEventListener('fullscreenchange', this.onFullscreenChange);
      document.addEventListener('webkitfullscreenchange', this.onFullscreenChange);
      // iPhone plays fullscreen video in its own layer and reports it only here.
      this.video.addEventListener('webkitendfullscreen', () => {
        this.video.controls = false;
      });
    }

    /*
      Sound is off to begin with, and not only as a courtesy: muted is the
      condition browsers put on autoplay, so a video that starts with sound would
      simply refuse to start at all.
    */
    #wireSound() {
      this.classList.toggle('is-muted', this.video.muted);

      this.soundButton.addEventListener('click', () => {
        this.video.muted = !this.video.muted;
        // Turning the sound on for a still frame gives nothing to hear; take it
        // as the cue to start.
        if (!this.video.muted && this.video.paused) this.#play();
      });

      // Fullscreen has its own volume control, so the state can change without
      // our button being touched.
      this.video.addEventListener('volumechange', () => {
        this.classList.toggle('is-muted', this.video.muted);
      });
    }

    /* Brings the video out from behind its cover without claiming it is playing. */
    #showVideo() {
      if (!this.classList.contains('is-playing')) this.classList.add('is-paused');
    }

    #watchForAutoplay() {
      // Someone who asked for less motion gets the still and a button.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            if (this.autoplayed) return;
            this.autoplayed = true;
            this.#play();
          } else if (!this.video.paused) {
            // Off screen and still running is just a battery drain.
            this.video.pause();
          }
        },
        { threshold: 0.5 }
      );
      this.observer.observe(this);
    }

    #toggle() {
      if (!this.video.paused) {
        this.video.pause();
        return;
      }
      // Anything that has finished starts over; anything paused picks up.
      if (this.video.ended) this.video.currentTime = 0;
      this.#play();
    }

    #play() {
      // A blocked play() (data saver, battery saver) leaves the cover and the
      // play button exactly as they were, which is the honest outcome.
      this.video.play().catch(() => {});
    }

    #state(state) {
      this.classList.toggle('is-playing', state === 'playing');
      this.classList.toggle('is-paused', state === 'paused');
      this.classList.toggle('is-ended', state === 'ended');
    }
  }

  if (!customElements.get('media-player')) {
    customElements.define('media-player', MediaPlayer);
  }
})();
